import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DOMPurify from "dompurify";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, FileText, Plus, Eye, Shield, CheckCircle2, Clock, Users, Upload, Loader2 } from "lucide-react";

export default function AgreementManagement() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [signedAgreements, setSignedAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewAgreement, setViewAgreement] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", notes: "" });
  const [tab, setTab] = useState("templates");

  // Upload external agreement state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ client_name: "", client_email: "", legal_name: "", title: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, agreementsRes] = await Promise.all([
        apiClient.get("/admin/agreement-templates"),
        apiClient.get("/agreements"),
      ]);
      setTemplates(templatesRes.data);
      setSignedAgreements(agreementsRes.data);
    } catch (error) {
      toast.error("Failed to load agreement data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/admin/agreement-templates", form);
      toast.success("Agreement template created");
      setCreateOpen(false);
      setForm({ title: "", content: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    }
  };

  const handleViewAgreement = async (id) => {
    try {
      const res = await apiClient.get(`/agreements/${id}`);
      setViewAgreement(res.data);
      setViewOpen(true);
    } catch (error) {
      toast.error("Failed to load agreement");
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await apiClient.get(`/agreements/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `SAG_Agreement_${id.slice(0, 8)}.pdf`;
      link.click();
    } catch (error) {
      toast.error("PDF not available yet");
    }
  };

  const handleUploadExternal = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Please attach a signed PDF"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("client_name", uploadForm.client_name);
      formData.append("client_email", uploadForm.client_email);
      formData.append("legal_name", uploadForm.legal_name);
      formData.append("title", uploadForm.title);

      await apiClient.post("/admin/agreements/upload-external", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("External agreement uploaded and linked");
      setUploadOpen(false);
      setUploadForm({ client_name: "", client_email: "", legal_name: "", title: "" });
      setUploadFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-slate-500">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading text-slate-900">Service Agreements</h1>
              <p className="text-slate-500 mt-1">Manage agreement templates and view signed agreements</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setUploadOpen(true)} data-testid="upload-external-agreement">
              <Upload className="h-4 w-4 mr-2" /> Upload Signed Agreement
            </Button>
            <Button className="bg-sag-navy hover:bg-sag-navy-800" onClick={() => setCreateOpen(true)} data-testid="create-template-button">
              <Plus className="h-4 w-4 mr-2" /> New Template Version
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-1">
          <Button variant={tab === "templates" ? "default" : "ghost"} size="sm" onClick={() => setTab("templates")} className={tab === "templates" ? "bg-sag-navy" : ""} data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" /> Templates ({templates.length})
          </Button>
          <Button variant={tab === "signed" ? "default" : "ghost"} size="sm" onClick={() => setTab("signed")} className={tab === "signed" ? "bg-sag-navy" : ""} data-testid="tab-signed">
            <Shield className="h-4 w-4 mr-2" /> Signed Agreements ({signedAgreements.length})
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="sag-card">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-sag-navy/10 text-sag-navy"><FileText className="h-5 w-5" /></div>
              <div>
                <p className="text-2xl font-bold">{templates.length}</p>
                <p className="text-sm text-slate-500">Template Versions</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-5 w-5" /></div>
              <div>
                <p className="text-2xl font-bold">{signedAgreements.length}</p>
                <p className="text-sm text-slate-500">Signed Agreements</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100 text-amber-600"><Users className="h-5 w-5" /></div>
              <div>
                <p className="text-2xl font-bold">{new Set(signedAgreements.map(a => a.client_id)).size}</p>
                <p className="text-sm text-slate-500">Unique Clients</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates Tab */}
        {tab === "templates" && (
          <Card className="sag-card" data-testid="templates-table">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg">Agreement Templates</CardTitle>
              <CardDescription>Each content change creates a new version for legal traceability</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell><Badge variant="secondary">v{t.version}</Badge></TableCell>
                      <TableCell className="font-medium">{t.title}</TableCell>
                      <TableCell className="text-sm text-slate-500">{t.created_by_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {t.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-slate-400">Archived</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Signed Agreements Tab */}
        {tab === "signed" && (
          <Card className="sag-card" data-testid="signed-agreements-table">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg">Signed Agreements</CardTitle>
              <CardDescription>Complete audit trail for chargeback defense</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {signedAgreements.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No agreements signed yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Legal Name</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Signed At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signedAgreements.map((a) => (
                      <TableRow key={a.id} data-testid={`agreement-${a.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{a.client_name}</p>
                            <p className="text-xs text-slate-500">{a.client_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium italic text-sag-navy">{a.full_legal_name}</TableCell>
                        <TableCell><Badge variant="secondary">v{a.template_version}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-500 font-mono">{a.ip_address}</TableCell>
                        <TableCell className="text-sm text-slate-500">{new Date(a.signed_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleViewAgreement(a.id)} data-testid={`view-agreement-${a.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(a.id)} data-testid={`download-pdf-${a.id}`}>
                              <FileText className="h-4 w-4 text-blue-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Template Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Agreement Template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="SAG Service Agreement" data-testid="template-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Agreement Content (HTML)</Label>
                <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={15} placeholder="Paste your agreement text here..." className="font-mono text-sm" data-testid="template-content-input" />
              </div>
              <div className="space-y-2">
                <Label>Internal Notes (optional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What changed in this version..." data-testid="template-notes-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="save-template-button">Create Version</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Signed Agreement Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Signed Agreement — Audit Record</DialogTitle>
            </DialogHeader>
            {viewAgreement && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Agreement ID:</span> <span className="font-mono">{viewAgreement.id}</span></div>
                  <div><span className="text-slate-500">Version:</span> <Badge variant="secondary">v{viewAgreement.template_version}</Badge></div>
                  <div><span className="text-slate-500">Client:</span> {viewAgreement.client_name}</div>
                  <div><span className="text-slate-500">Email:</span> {viewAgreement.client_email}</div>
                  <div><span className="text-slate-500">Legal Name:</span> <strong className="text-sag-navy italic">{viewAgreement.full_legal_name}</strong></div>
                  <div><span className="text-slate-500">Signed At:</span> {new Date(viewAgreement.signed_at).toLocaleString()} UTC</div>
                  <div><span className="text-slate-500">IP Address:</span> <span className="font-mono">{viewAgreement.ip_address}</span></div>
                  <div><span className="text-slate-500">Browser:</span> <span className="text-xs">{viewAgreement.browser_info?.slice(0, 60)}...</span></div>
                </div>
                <div className="space-y-1 text-sm bg-emerald-50 rounded-lg p-3">
                  <p className="font-medium text-emerald-800">Acknowledgments:</p>
                  <p className="text-emerald-700">[X] Scope of services confirmed</p>
                  <p className="text-emerald-700">[X] Non-refundable policy acknowledged</p>
                  <p className="text-emerald-700">[X] Service timelines accepted</p>
                  <p className="text-emerald-700">[X] Chargeback dispute waiver signed</p>
                </div>
                <div className="border rounded-lg p-4 max-h-60 overflow-y-auto text-sm bg-slate-50" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewAgreement.agreement_content) }} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload External Signed Agreement Dialog */}
        <Dialog open={uploadOpen} onOpenChange={(open) => { if (!open) { setUploadOpen(false); setUploadFile(null); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-emerald-600" /> Upload Signed Agreement</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500">Upload a pre-signed agreement PDF for a client (e.g., contracts signed offline or via email).</p>
            <form onSubmit={handleUploadExternal} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input value={uploadForm.client_name} onChange={(e) => setUploadForm({ ...uploadForm, client_name: e.target.value })} placeholder="e.g., JDL Cargo" required data-testid="upload-client-name" />
                </div>
                <div className="space-y-2">
                  <Label>Client Email *</Label>
                  <Input type="email" value={uploadForm.client_email} onChange={(e) => setUploadForm({ ...uploadForm, client_email: e.target.value })} placeholder="e.g., client@company.com" required data-testid="upload-client-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Legal Signatory Name *</Label>
                <Input value={uploadForm.legal_name} onChange={(e) => setUploadForm({ ...uploadForm, legal_name: e.target.value })} placeholder="Name of person who signed" required data-testid="upload-legal-name" />
              </div>
              <div className="space-y-2">
                <Label>Agreement Title *</Label>
                <Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} placeholder="e.g., JDL Cargo — SAG Addendum" required data-testid="upload-title" />
              </div>
              <div className="space-y-2">
                <Label>Signed PDF *</Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="upload-agreement-file" />
                <p className="text-xs text-slate-400">PDF only (max 10MB)</p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading || !uploadFile} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-upload-agreement">
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading...</> : <><Upload className="h-4 w-4 mr-2" /> Upload Agreement</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
